import React, { useState, useEffect } from 'react';
import EmptyState from '../../components/EmptyState';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Person,
  Phone,
  Email,
  Badge,
  Work,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';

const NonTeachingStaff = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, staffMember: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await principalAPI.getStaff();
        if (isMounted) {
          setStaff(response.data.staff || []);
        }
      } catch (error) {
        console.error('Error fetching staff:', error);
        if (isMounted) {
          setStaff([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleExpand = (staffId) => {
    setExpandedId(expandedId === staffId ? null : staffId);
  };

  const handleEdit = (e, staffMember) => {
    e.stopPropagation();
    navigate(`/principal/non-teaching-staff/${staffMember.id}/edit`, { state: { staff: staffMember } });
  };

  const handleDeleteClick = (e, staffMember) => {
    e.stopPropagation();
    setDeleteDialog({ open: true, staffMember });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.staffMember) return;
    setDeleteLoading(true);
    try {
      await principalAPI.deleteStaff(deleteDialog.staffMember.id);
      setStaff(staff.filter(s => s.id !== deleteDialog.staffMember.id));
      if (expandedId === deleteDialog.staffMember.id) setExpandedId(null);
      setDeleteDialog({ open: false, staffMember: null });
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert(error.response?.data?.message || 'Failed to delete staff member');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const DetailItem = ({ icon, label, value }) => {
    if (!value) return null;
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        {icon}
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
          {label}:
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Non-Teaching Staff
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/principal/non-teaching-staff/new')}
          >
            Add Staff Member
          </Button>
        </Box>
      </Box>

      {/* Staff List */}
      {staff.length === 0 ? (
        <EmptyState
          icon={Person}
          title="No staff members found"
          description='Click "Add Staff Member" to create one.'
          actionLabel="Add Staff Member"
          onAction={() => navigate('/principal/non-teaching-staff/new')}
        />
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {staff.map((staffMember) => {
            const isExpanded = expandedId === staffMember.id;
            const name = staffMember.user?.name || 'N/A';
            return (
              <Paper
                key={staffMember.id}
                elevation={isExpanded ? 4 : 1}
                sx={{
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: isExpanded ? '1px solid' : '1px solid transparent',
                  borderColor: isExpanded ? 'primary.main' : 'transparent',
                  '&:hover': { boxShadow: 3 },
                }}
                onClick={() => toggleExpand(staffMember.id)}
              >
                {/* Staff Summary Row */}
                <Box display="flex" alignItems="center" px={2.5} py={1.5} gap={2}>
                  {/* Expand Icon */}
                  <Box sx={{ color: 'text.secondary' }}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f5576c 0%, #e91e63 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold" color="white">
                      {name.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>

                  {/* Name & Employee ID */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {staffMember.employee_id || 'No ID'}
                    </Typography>
                  </Box>

                  {/* Designation Chip */}
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {staffMember.designation ? (
                      <Chip
                        label={staffMember.designation}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="No Designation" size="small" variant="outlined" />
                    )}
                  </Box>

                  {/* Phone */}
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {staffMember.user?.phone || '—'}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Edit Staff">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => handleEdit(e, staffMember)}
                        sx={{
                          bgcolor: 'primary.50',
                          '&:hover': { bgcolor: 'primary.100' },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Staff">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(e, staffMember)}
                        sx={{
                          bgcolor: 'error.50',
                          '&:hover': { bgcolor: 'error.100' },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Expanded Details */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box px={3} py={2.5} sx={{ bgcolor: 'grey.50' }}>
                    <Grid container spacing={3}>
                      {/* Personal Information */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Personal Information
                        </Typography>
                        <DetailItem
                          icon={<Person sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Full Name"
                          value={staffMember.user?.name}
                        />
                        <DetailItem
                          icon={<Email sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Email"
                          value={staffMember.user?.email}
                        />
                        <DetailItem
                          icon={<Phone sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Phone"
                          value={staffMember.user?.phone}
                        />
                      </Grid>

                      {/* Work Information */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Work Information
                        </Typography>
                        <DetailItem
                          icon={<Badge sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Employee ID"
                          value={staffMember.employee_id}
                        />
                        <DetailItem
                          icon={<Work sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Designation"
                          value={staffMember.designation}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Results Summary */}
      {staff.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Showing {staff.length} staff member{staff.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, staffMember: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Delete Staff Member
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.staffMember?.user?.name}</strong>
            {deleteDialog.staffMember?.employee_id && (
              <> (Employee ID: <strong>{deleteDialog.staffMember.employee_id}</strong>)</>
            )}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, staffMember: null })} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NonTeachingStaff;
